import {useFormContext} from 'react-hook-form'
import {useEffect, useMemo} from 'react'

import {AspectRatioEnum, BlockKeyFactVisualizationEnum} from '@sdox/api'

import {EditorFeature, TemplateInputItem} from '../../../../'

import type {DynamicFieldData, SelectFieldData} from '../../../../'

const aspectFieldData: SelectFieldData = {
  inputType: 'select',
  options: [
    {label: '1:1', value: AspectRatioEnum.Aspect_1_1},
    {label: '3:2', value: AspectRatioEnum.Aspect_3_2},
    {label: '4:3', value: AspectRatioEnum.Aspect_4_3},
    {label: '16:9', value: AspectRatioEnum.Aspect_16_9},
    {label: '16:10', value: AspectRatioEnum.Aspect_16_10},
  ],
  fieldName: 'data.aspectRatio',
  label: 'Bildformat',
  defaultValue: AspectRatioEnum.Aspect_3_2,
  required: true,
}

const visualizationFieldData: SelectFieldData = {
  inputType: 'select',
  options: [
    {label: 'Mit Bild', value: BlockKeyFactVisualizationEnum.WithImage},
    {label: 'Ohne Bild', value: BlockKeyFactVisualizationEnum.WithoutImage},
  ],
  fieldName: 'data.visualization',
  label: 'Darstellung',
  defaultValue: BlockKeyFactVisualizationEnum.WithImage,
  required: true,
}

const getKeyFactFields = (visualization: BlockKeyFactVisualizationEnum): DynamicFieldData => {
  const keyFactFields: DynamicFieldData = {
    label: 'Key Fact',
    inputType: 'array',
    required: true,
    description: '',
    fieldName: 'data.items',
    defaultValue: undefined,
    fields: [
      {
        label: 'Titel',
        fieldName: 'title',
        inputType: 'richtext',
        editorConfig: {
          spanAsParagraph: true,
          features: [
            EditorFeature.nobreak,
            EditorFeature.footnote,
          ],
        },
        defaultValue: '',
        required: true,
      },
      {
        label: 'Text',
        fieldName: 'text',
        inputType: 'richtext',
        editorConfig: {
          features: [
            EditorFeature.footnote,
            EditorFeature.linebreak,
            EditorFeature.nobreak,
            EditorFeature.bold,
            EditorFeature.italic,
          ],
        },
        defaultValue: '',
        required: true,
      },
      {
        label: 'Button',
        fieldName: 'cta',
        inputType: 'button',
        defaultValue: 'none',
        required: false,
      },
    ],
  }

  if (visualization === BlockKeyFactVisualizationEnum.WithImage) {
    keyFactFields.fields.unshift({
      label: 'Bild',
      fieldName: 'image',
      inputType: 'image',
      defaultValue: '',
      required: true,
    })
  }

  return keyFactFields
}

export const KeyFactForm = () => {
  const {watch, unregister, getValues} = useFormContext()
  const visualization: BlockKeyFactVisualizationEnum = watch('data.visualization')

  useEffect(() => {
    if (visualization === BlockKeyFactVisualizationEnum.WithoutImage) {
      const values = getValues()
      const items: unknown[] = values.data.items
      items.forEach((_, index) => unregister(`data.items.${index}.image`))
      unregister('data.aspectRatio')
    }
  }, [visualization, unregister, getValues])

  const keyFactFields = useMemo(() => getKeyFactFields(visualization), [visualization])

  return (
    <div className={'grid gap-spacing-12'}>
      <TemplateInputItem data={visualizationFieldData} />
      {visualization === BlockKeyFactVisualizationEnum.WithImage ? <TemplateInputItem data={aspectFieldData} /> : null}
      <TemplateInputItem data={keyFactFields} />
    </div>
  )
}
